import axios from 'axios'
import {join, concat, omit, reject, anyPass, isNil} from 'ramda'
import createAuth0Client, {
  Auth0Client,
  Auth0ClientOptions
} from '@auth0/auth0-spa-js'
import {WebAuth} from 'auth0-js'
import {Amplify, Auth, Storage} from 'aws-amplify'
import {generate} from 'generate-password'
import {CognitoHostedUIIdentityProvider} from '@aws-amplify/auth'
import AnalyticsUtils from '../../analytics/utils'

import {logAndCaptureErrorMessage} from '../../utils/logging/errorHandlers'
import {LOGIN_CALLBACK_ROUTE} from '../constants'

import {SendMessageParams} from '../types/Passwordless'
import {
  AuthConnectionWithAudience,
  UserLastConnectionMethod
} from 'auth/types/Auth'
import {UserAttributes, CognitoSession, User} from 'auth/types/Cognito'
import {getAmplifyConfig} from '../amplifyConfig/getConfig'
import {SocialConnections} from '../modules/thunks'
import { USER_TIERS } from 'offer/business/privateDeals'

export type AuthClientType = Auth0Client | null | undefined

declare global {
  interface Window {
    authClient: Auth0Client | null
    grecaptcha: {enterprise: Record<string, any>}
  }
}

const getCallbackUri = () =>
  concat(window.location.origin, LOGIN_CALLBACK_ROUTE)

/**
 * Auth client
 */
export const getAuthClient = async (
  options?: Auth0ClientOptions
): Promise<AuthClientType> => {
  // TODO (Engagement) [2022-09-01]: Remove authClient from window
  if (!window.authClient && options) {
    // `createAuth0Client` will crash when there's an invalid or expired token
    // When this happens, log the user out so they can continue using the app with a valid state.
    if (!navigator.userAgent.includes('jsdom')) {
      try {
        window.authClient = await createAuth0Client(options)
      } catch (error) {
        window.authClient = new Auth0Client(options)
        await window.authClient.logout({localOnly: true})

        logAndCaptureErrorMessage(error)
      }
    }
  }

  return window.authClient
}

const axiosInstance = axios.create()

export const getCustomHeader = (params: string[]) => join('|', params)

/**
 * Post passwordless to auth client
 * @param phoneNumber - users inputted phone number
 * @param languageCode - language
 * @param selectedChannel - via whatsapp or sms
 * @param brandCode - brand code
 * @param token - reCaptcha token
 * @param authConnection - auth connection
 */
export const passwordlessLoginService = async ({
  phoneNumber,
  languageCode,
  selectedChannel,
  brandCode,
  token,
  authConnection
}: SendMessageParams) => {
  const smsChannel = `${selectedChannel}_twilio`
  const channel = selectedChannel === 'sms' ? smsChannel : selectedChannel
  const requestUrl = `https://${authConnection.domain}/passwordless/start`

  const customHeader = getCustomHeader([languageCode, channel])

  const config = {
    headers: {
      'Content-Type': 'application/json',
      'x-request-language': customHeader
    }
  }

  return axiosInstance.post(
    requestUrl,
    {
      phone_number: phoneNumber,
      brand: brandCode,
      token
    },
    config
  )
}

/**
 * Init new auth0 instance
 */
export const getAuthSDK = (authConnection: AuthConnectionWithAudience) =>
  new WebAuth({
    domain: authConnection.domain,
    clientID: authConnection.clientId,
    audience: authConnection.audience,
    redirectUri: getCallbackUri(),
    responseType: 'token id_token'
  })

/**
 * Configure Amplify
 */
export const configureAmplify = () => {
  const amplifyConfig = getAmplifyConfig(location.origin)
  Amplify.configure(amplifyConfig)
}

/**
 * Amplify SignIn
 */
export const signInService = async ({
  phoneNumber,
  token,
  selectedChannel,
  languageCode,
  brandCode
}: SendMessageParams) => {
  try {
    const user = await Auth.signIn({
      password: '',
      username: phoneNumber
    })

    await Auth.sendCustomChallengeAnswer(user, 'SEND_CONTEXT', {
      token,
      languageCode,
      method: selectedChannel.toUpperCase(),
      brandCode
    })

    return user
  } catch (error) {
    console.error('Cognito: signInService:', error)
    throw new Error(error)
  }
}

/**
 * Amplify SignUp service
 */
export const signUpService = async ({
  phoneNumber,
  token
}: Pick<SendMessageParams, 'phoneNumber' | 'token'>) => {
  const password = generate({
    length: 24,
    numbers: true,
    symbols: true,
    lowercase: true,
    uppercase: true,
    strict: true
  })
  return Auth.signUp({
    username: phoneNumber,
    password,
    validationData: {token}
  })
}

/**
 * Amplify SignOut
 */
export const signOutService = async () => {
  await Auth.signOut()
  AnalyticsUtils.resetAnalytics()
}

/**
 * Amplify Custom Challenge Answer
 */
export const answerCustomChallengeService = async (
  user: CognitoSession,
  code: string
): Promise<User> => {
  await Auth.sendCustomChallengeAnswer(user, code)

  return getCurrentUser()
}

/**
 * Amplify Federated Social Login
 */
export const socialLoginService = async (
  connection: CognitoHostedUIIdentityProvider
) => {
  await Auth.federatedSignIn({
    provider: connection
  })
}

/**
 * Amplify Get current user
 */
export const getCurrentUser = async (): Promise<User | null> => {
  try {
    configureAmplify()

    const [currentSession, currentUser] = await Promise.all([
      Auth.currentSession(),
      Auth.currentUserInfo()
    ])

    const idToken = currentSession.getIdToken()
    const {createdAt, updatedAt} = idToken.payload
    const user = {
      ...currentUser,
      createdAt,
      updatedAt
    }

    return user
  } catch (error) {
    console.error('Cognito: Error getting current user', error)
    return null
  }
}

/**
 * Get current authenticated user from Amplify Auth SDK
 */
export const getCurrentAuthenticatedUserService = async (): Promise<User> => {
  return Auth.currentAuthenticatedUser()
}

/**
 * update user attribute of the current authenticated user
 * @param user current authenticated user
 * @param attributes attributes to update
 */
export const updateUserAttributesService = async (
  user: User,
  attributes: UserAttributes
): Promise<'SUCCESS' | string> => {
  const attributesToUpdate = reject(anyPass([isNil]))(
    omit(['email', 'phone_number'], attributes)
  )

  return Auth.updateUserAttributes(user, attributesToUpdate)
}

/**
 * upgrade current authenticated user to specific tier
 * this function does not overwrite previous attributes nor tiers
 * @param user current authenticated user
 * @param tier the new tier ex: USER_TIERS.plus
 */
 export const upgradeUserTier = async (
  user: User,
  tier: USER_TIERS
): Promise<'SUCCESS' | string> => {
  const appData = JSON.parse(user.attributes['custom:app_data'] || '{}')

  const previousTiers = appData?.tier || []

  const newAppData = JSON.stringify({
    ...appData,
    tier: [...new Set([...previousTiers, tier])]
  })

  await updateUserAttributesService(user, {'custom:app_data': newAppData})
  return newAppData
}

/**
 * put an object in profile picture S3 bucket by using Amplify Storage SDK
 * @param key key of the object in the bucket
 * @param object object to put in the bucket (File)
 * @param config configuration
 */
export const putStorageService = async (
  key: string,
  object,
  config
): Promise<any> => {
  return Storage.put(key, object, config)
}

/**
 * get an object from profile picture S3 bucket by using Amplify Storage SDK
 * @param key key of the object in the bucket
 * @param config configuration
 */
export const getStorageService = async (key: string, config): Promise<any> => {
  return Storage.get(key, config)
}

/**
 * A function to set the last login method of the user
 * @param method UserLastConnectionMethod or 'social'
 */
export const updateLastLoginMethodService = async (
  method: UserLastConnectionMethod | 'social'
) => {
  try {
    const user = await Auth.currentAuthenticatedUser()
    const userDataAttribute = getUserDataAttribute(user, method)
    const attributes = {
      'custom:user_data': JSON.stringify(userDataAttribute)
    }
    return await Auth.updateUserAttributes(user, attributes)
  } catch (error) {
    console.error('Cognito: updateLastLoginMethodService:', error)
  }
}

export const getUserDataAttribute = (
  user,
  method: UserLastConnectionMethod | 'social'
) => {
  try {
    const lastConnectionMethod = 'lastConnectionMethod'
    const userDataAttribute = JSON.parse(
      user?.attributes?.['custom:user_data'] || '{}'
    )

    if (method === 'social' && user?.attributes?.identities) {
      const identities = JSON.parse(user?.attributes?.identities)
      const providerName = identities?.[0]?.providerName
      userDataAttribute[lastConnectionMethod] =
        providerName === 'Google'
          ? SocialConnections.Google
          : SocialConnections.Facebook
    } else {
      userDataAttribute[lastConnectionMethod] = method
    }

    return userDataAttribute
  } catch (error) {
    console.error('Cognito: getUserDataAttribute:', error)
    return {}
  }
}

export const getRecaptchaToken = async (): Promise<string> =>
  window.grecaptcha?.enterprise.execute()
